import { type DocumentReference } from 'firebase/firestore';

import { type RootStore } from 'stores/RootStore';
import { AnimationPanelViews } from 'types/AnimationPanelViews';
import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentStatus } from 'types/DocumentStatus';
import { DocumentType } from 'types/DocumentType';
import { nowOrServerTimestamp } from 'utils/FirestoreUtils';

import AnimationExporter from './AnimationExporter';
import AnimationPlayerStore from './AnimationPlayer';
import Assets from './Assets';
import CommentsManager from './CommentsManager';
import DocumentFolder, { applyDynamicFolderDefaults } from './DocumentFolder';
import type DocumentFolderCollection from './DocumentFolderCollection';
import { FolderSchema } from './DocumentFolderTypes';

export class AnimationSchema extends FolderSchema {
    type = DocumentType.ANIMATION;
    archived = false;
    author: string = '';
    currentAnimationID: string = '';
    name: string = '';
    notes: string = '';
    numComments: number = 0;
    commentsDateAdded = nowOrServerTimestamp();
    dateModified = nowOrServerTimestamp();
    tags: string[] = [];
    viewHistory: Record<string, { seconds: number }> = {};
}

export class AnimationPublicSchema {
    type = DocumentType.ANIMATION;
    name: string = '';
}

export default class Animation extends DocumentFolder<
    AnimationSchema,
    AnimationPublicSchema
> {
    // Animations are created either as a child of a project's
    // DocumentFolderCollection, or on their own by RootStore.loadAnimation.
    declare parent: DocumentFolderCollection | RootStore | undefined;

    schema = new AnimationSchema();
    publicSchema = new AnimationPublicSchema();
    type = DocumentType.ANIMATION;
    collectionType = DocumentCollectionType.ANIMATION;
    assets = new Assets(this);
    player = new AnimationPlayerStore();
    comments = new CommentsManager(this);
    exporter = new AnimationExporter(this);
    displayPanel: AnimationPanelViews = AnimationPanelViews.NONE;

    showBorder: boolean = false;
    backgroundColor: string = '#f0f2f5';
    showCheckerboard: boolean = false;

    // Overrides DocumentStore.getDefaults to build a fresh schema each
    // time, so dynamic values (timestamps, author) are current. Called by
    // _update() for missing fields, and by the collections when creating a
    // child; reset() still copies `this.schema` directly.
    public getDefaults() {
        const data = new AnimationSchema();
        applyDynamicFolderDefaults(data, this.parentFolder);
        return data;
    }

    get currentAnimationAsset() {
        return this.assets.getById(this.data.currentAnimationID);
    }

    constructor(
        parent: DocumentFolderCollection | RootStore,
        docRef?: DocumentReference
    ) {
        super(parent, docRef);
        this.comments.enable();
        this.becomeObservable();
    }

    // Action to toggle border
    toggleShowBorder = () => {
        this.showBorder = !this.showBorder;
    };

    // Action to set background color
    setBackgroundColor = (color: string) => {
        this.backgroundColor = color;
        this.showCheckerboard = false; // Turn off checkerboard when setting a color
    };

    // Action to toggle checkerboard
    toggleShowCheckerboard = () => {
        this.showCheckerboard = !this.showCheckerboard;
    };

    onLoad = async (_data: FolderSchema) => {
        if (this.stub) return;
        await Promise.all([
            this.breadcrumbs.load(),
            this.comments.load(),
            this.members.load(),
            this.assets.load(),
        ]);
        await this.exporter.load();
    };

    // ??? Not a no-op: this overrides DocumentFolder.onUpdated, whose only job
    // is `this.members.update()`, so animations never refresh members on
    // update. Creative's onUpdated also skips it - this looks like a copy of
    // Creative's with the body stripped. Deleting it would start running
    // members.update() on every animation update.
    onUpdated = () => {};

    setPanel = (panelView: AnimationPanelViews) => {
        this.displayPanel = panelView;
    };

    setPanelOrToggle = (panelView: AnimationPanelViews) => {
        this.setPanel(
            panelView === this.displayPanel
                ? AnimationPanelViews.NONE
                : panelView
        );
    };

    setCurrentAnimation = async (animationID: string) => {
        this.data.currentAnimationID = animationID;
        await this.save();
        await this.exporter.load();
    };

    /**
     * Uploads `file` as a new version and makes it the current animation.
     */
    uploadAnimationFile = async (file: File) => {
        // TODO: the original comment here read "create utc timestamp based
        // name below", describing intent that was never implemented. Nothing
        // renames the asset, so uploading animation.json three times gives
        // three version rows all displaying "animation.json". The hook for it
        // is the `overrideFileName` upload option, already used by
        // DynamicProperties/Types/Group. Note this is
        // display only -- storage paths are already deduped by the Date.now()
        // prefix in uploadToAssetFolder.
        const uploadedAsset = await this.assets.uploadToCurrentAssetFolder(
            file,
            {
                postProcess: false,
            }
        );
        await this.setCurrentAnimation(uploadedAsset.id);
    };

    reset() {
        super.reset();
        this.status = DocumentStatus.NONE;
        this.data = new AnimationSchema();
        this.player.reset();
        this.comments.reset();
        this.assets.reset();
        this.assets = new Assets(this);
        this.displayPanel = AnimationPanelViews.NONE;
        this.exporter.reset();
    }

    get newCommentsCount() {
        return this.comments.newCommentsCount;
    }
}
